import { createContext, useState, ReactNode, useEffect } from "react";
import { Links } from "components/sidebar/types";
import mainSidebarItems from "components/sidebar/sidebar-links/main-sidebar";
import { systemSettingGlobalSettingLinks, systemSettingAdminSettingLinks } from "components/sidebar/sidebar-links/system-settings-sidebar";
import adminSidebarLinks from "components/sidebar/sidebar-links/admin-sidebar";
import { useRouter } from "next/router";
import useFirstSidebarPage from "hooks/useFirstSidebarPage";
import useFlag from "hooks/useFlag";

const mainSidebarItemsClone = { ...mainSidebarItems };
const systemSettingGlobalSettingLinksClone = { ...systemSettingGlobalSettingLinks };
const systemSettingAdminSettingLinksClone = { ...systemSettingAdminSettingLinks };
const adminSidebarLinksClone = { ...adminSidebarLinks };

// ** Types
interface SidebarContextType {
  lastViewedPage: string;
  setLastViewedPage: (lastViewedPage: string) => void;
  lastViewedSettingsPage: string;
  setLastViewedSettingsPage: (lastViewedSettingsPage: string) => void;
  isSystemSettings: boolean | null;
  setIsSystemSettings: (isSystemSettings: boolean | null) => void;
  systemSettingsActiveTab: "global" | "admin";
  setSystemSettingsActiveTab: (systemSettingsActiveTab: "global" | "admin") => void;
  isAdminRoutes: boolean | null;
  setIsAdminRoutes: (isAdminRoutes: boolean | null) => void;
  mainSidebarLinksOpen: Links;
  setMainSidebarLinksOpen: (mainSidebarLinksOpen: Links) => void;
  globalSettingsLinksOpen: Links;
  setGlobalSettingsLinksOpen: (globalSettingsLinksOpen: Links) => void;
  adminSettingsLinksOpen: Links;
  setAdminSettingsLinksOpen: (adminSettingsLinksOpen: Links) => void;
  adminLinksOpen: Links;
  setAdminLinksOpen: (adminLinksOpen: Links) => void;
  initialOpenSet: boolean;
  setInitialOpenSet: (initialOpenSet: boolean) => void;
  logoUrl: string | null;
  setLogoUrl: (logoUrl: string | null) => void;
}

interface SidebarProviderProps {
  children: ReactNode;
}

// ** Default values
const defaultStoreValues: SidebarContextType = {
  lastViewedPage: "/",
  setLastViewedPage: () => null,
  lastViewedSettingsPage: "/",
  setLastViewedSettingsPage: () => null,
  isSystemSettings: null,
  systemSettingsActiveTab: "global",
  setSystemSettingsActiveTab: () => null,
  setIsSystemSettings: () => null,
  isAdminRoutes: null,
  setIsAdminRoutes: () => null,
  mainSidebarLinksOpen: mainSidebarItemsClone,
  setMainSidebarLinksOpen: () => null,
  globalSettingsLinksOpen: systemSettingGlobalSettingLinksClone,
  setGlobalSettingsLinksOpen: () => null,
  adminSettingsLinksOpen: systemSettingAdminSettingLinksClone,
  setAdminSettingsLinksOpen: () => null,
  adminLinksOpen: adminSidebarLinksClone,
  setAdminLinksOpen: () => null,
  initialOpenSet: false,
  setInitialOpenSet: () => null,
  logoUrl: null,
  setLogoUrl: () => null,
};

// ** Create Context
const SidebarContext = createContext<SidebarContextType>(defaultStoreValues);

const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const adminSettingLinksBasedOnFlags = (): Links => {
    const systemSettingAdminSettingLinksToUse: Links = systemSettingAdminSettingLinksClone;

    return systemSettingAdminSettingLinksToUse;
  };

  const mainSidebarItemsBaseOnFlags = (): Links => {
    const mainSidebarItemsBaseLinksToUse: Links = mainSidebarItemsClone;

    return mainSidebarItemsBaseLinksToUse;
  };

  const systemSettingAdminSettingLinksToUse = adminSettingLinksBasedOnFlags();

  // get the first valid page on the regular sidebar and first on the system settings sidebar - these will depend on the user's Role and permissions
  const firstValidPageRoute = useFirstSidebarPage({});
  const firstValidSystemSettingsPageRoute = useFirstSidebarPage({ systemSettings: true });

  const [lastViewedPage, setLastViewedPage] = useState(firstValidPageRoute);
  const [lastViewedSettingsPage, setLastViewedSettingsPage] = useState(firstValidSystemSettingsPageRoute);
  const [isSystemSettings, setIsSystemSettings] = useState<boolean | null>(null);
  const [systemSettingsActiveTab, setSystemSettingsActiveTab] = useState<"global" | "admin">(defaultStoreValues.systemSettingsActiveTab);
  const [isAdminRoutes, setIsAdminRoutes] = useState<boolean | null>(null);
  const [initialOpenSet, setInitialOpenSet] = useState(false);
  const [mainSidebarLinksOpen, setMainSidebarLinksOpen] = useState(mainSidebarItemsBaseOnFlags);
  const [globalSettingsLinksOpen, setGlobalSettingsLinksOpen] = useState(systemSettingGlobalSettingLinksClone);
  const [adminSettingsLinksOpen, setAdminSettingsLinksOpen] = useState(systemSettingAdminSettingLinksToUse);
  const [adminLinksOpen, setAdminLinksOpen] = useState(adminSidebarLinksClone);
  const [logoUrl, setLogoUrl] = useState<string | null>(defaultStoreValues.logoUrl);

  const router = useRouter();

  const handleSetDefaultPage = () => {
    const url = router.asPath;

    if (url.includes("system-settings")) {
      setLastViewedSettingsPage(url);
      return;
    }

    setLastViewedPage(url);
  };

  useEffect(() => {
    // TODO: TECH DEBT CLEANUP
    handleSetDefaultPage();
  }, []);

  useEffect(() => {
    const systemSettingAdminSettingLinksToUse = adminSettingLinksBasedOnFlags();
    // TODO: TECH DEBT CLEANUP
    setAdminSettingsLinksOpen(systemSettingAdminSettingLinksToUse);
    setInitialOpenSet(false);
  }, []);

  const values = {
    lastViewedPage,
    setLastViewedPage,
    lastViewedSettingsPage,
    setLastViewedSettingsPage,
    isSystemSettings,
    setIsSystemSettings,
    systemSettingsActiveTab,
    setSystemSettingsActiveTab,
    isAdminRoutes,
    setIsAdminRoutes,
    mainSidebarLinksOpen,
    setMainSidebarLinksOpen,
    globalSettingsLinksOpen,
    setGlobalSettingsLinksOpen,
    adminSettingsLinksOpen,
    setAdminSettingsLinksOpen,
    adminLinksOpen,
    setAdminLinksOpen,
    initialOpenSet,
    setInitialOpenSet,
    logoUrl,
    setLogoUrl,
  };

  return <SidebarContext.Provider value={values}>{children}</SidebarContext.Provider>;
};

export { SidebarContext, SidebarProvider };
